import * as React from 'react';
import * as styles from './styles.scss';
import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import type { PeoplePayload } from '../schema';
import loc from 'owa-localize';
import usePeopleViewState from '../hooks/usePeopleViewState';
import { ControlIcons } from 'owa-control-icons';
import { peopleAssetPlaceholder } from '../people.locstring.json';
import FindPeopleControl from 'owa-readwrite-recipient-well-fabric/lib/components/FindPeopleControl';
import ReadWriteRecipient from 'owa-readwrite-recipient-well/lib/components/ReadWriteRecipient';
import { ReadWriteRecipientWellSize } from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import getPersonaState from '../utils/getPersonaState';
import { PersonaIcon, PhoneNumber } from 'owa-timestream-asset-sdk';
import { Link } from '@fluentui/react/lib/Link';
import { Text } from '@fluentui/react/lib/Text';
import { PEOPLE_CARD_SCENARIO_NAME } from 'owa-substrate-people-suggestions/lib/services/substrateSuggestions';

export default observer(function PeopleView({ payload }: { payload: PeoplePayload }) {
    const {
        onPersonSelected,
        removePerson,
        viewState,
        pickerRef,
        isPickerVisible,
        setPickerVisible,
        name,
        emailAddress,
        title,
        department,
        phoneNumber,
        officeLocation,
        onEnterKeyDown,
        composePopoutMail,
    } = usePeopleViewState(payload);

    return (
        <div className={styles.peoplePickerContainer}>
            {!payload?.selectedPerson && (
                <div className={styles.peoplePicker}>
                    <Icon iconName={ControlIcons.Contact} className={styles.contactIcon} />
                    <FindPeopleControl
                        onItemSelected={onPersonSelected}
                        viewState={viewState}
                        focusAutomatically={false}
                        openPickerAutomatically={false}
                        inputAriaLabel={loc(peopleAssetPlaceholder)}
                        placeholder={loc(peopleAssetPlaceholder)}
                        scenario={PEOPLE_CARD_SCENARIO_NAME}
                    />
                </div>
            )}

            {isPickerVisible && !!payload?.selectedPerson && (
                <div className={styles.recipient} ref={pickerRef}>
                    <ReadWriteRecipient
                        viewState={getPersonaState(payload?.selectedPerson)}
                        expandGroupOperation={expandGroup}
                        size={ReadWriteRecipientWellSize.Regular}
                        removeOperation={removePerson}
                        index={0}
                        useContextMenu={true}
                        canDrag={false}
                        maxItemWidth={130}
                        isSelected={false}
                    />
                </div>
            )}

            {!isPickerVisible && !!payload?.selectedPerson && (
                <div
                    onClick={setPickerVisible}
                    className={styles.personaContainer}
                    tabIndex={0}
                    onKeyDown={onEnterKeyDown}>
                    <PersonaIcon
                        name={name}
                        emailAddress={emailAddress}
                        personaCardBehaviorDisabled={true}
                        className={styles.personaIcon}
                    />
                </div>
            )}

            {!!payload?.selectedPerson && (
                <div>
                    <Text
                        className={styles.personJobDetails}
                        nowrap={true}
                        block={true}
                        tabIndex={0}>
                        {title}
                    </Text>

                    <Text className={styles.detailRowText} nowrap={true} block={true} tabIndex={0}>
                        {department}
                    </Text>

                    <div className={styles.personDetails}>
                        <Icon iconName={ControlIcons.Mail} className={styles.detailIcons} />
                        <Link onClick={composePopoutMail} className={styles.detailRowLink}>
                            {emailAddress}
                        </Link>
                    </div>

                    <PhoneNumber
                        phoneNumberText={phoneNumber}
                        containerClassName={styles.personDetails}
                        iconClassName={styles.detailIcons}
                        phoneNumberTextClassName={styles.detailRowText}
                    />

                    <div className={styles.personDetails}>
                        <Icon iconName={ControlIcons.POI} className={styles.detailIcons} />
                        <Text
                            className={styles.detailRowText}
                            nowrap={true}
                            block={true}
                            tabIndex={0}>
                            {officeLocation}
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
});

function expandGroup() {
    // NotImplemented: Not applicable for asset as group persona pill should not be expanded
}
